import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { take } from 'rxjs/operators';
import { Project } from '@app/models/firebase/project';
import firebase from 'firebase/compat/app';
import { User } from '@app/models/firebase/user';
import { isArray, isNoEmptyArray, isNotEmptyString, isNullOrUndefined, isObject, isValidString } from '@pineapplelab/util';
import { ApiService } from '../api/api.service';
import { DbService } from '../db/db.service';
import { Auth } from '@app/models/auth';
import { SpinnerService } from '../spinner/spinner.service';
import { ToasterService } from '../toaster/toaster.service';
import { NavigationService } from '../navigation/navigation.service';
import { BrowserStorage } from '@app/models/browserStorage';
import { SignUpDto } from '@app/models/dto/signUp.dto';
import { TokenService } from '../token/token.service';
import { UpdatePasswordDto } from '@app/models/dto/updatePassword.dto';
import { ForgotPasswordDTO } from '@app/models/dto/forgotPassword.dto';
import { LoginByAccessTokenIdDto } from '@app/models/dto/loginByAccessTokenId.dto';
import { LoginDTO } from '../../models/dto/login.dto';
import { API_URLS } from '@app/utils/urls';
import { UserRole } from '@app/models/firebase/userRole';
import { SignUpTokenDto } from '@app/models/dto/signUpToken.dto';
import { TranslateService } from '@ngx-translate/core';
import { Employee } from '@app/models/firebase/employee';
import { PermissionEnum } from '@app/enums/permissions.enum';
import { environment } from '@environments/environment';

export interface ClientPortalCompanyOption {
  clientId: string;
  contactId: string;
  clientName: string;
}

// Flat (strictNullChecks is off): either a signed-in redirect or a list of companies to choose from.
export interface ClientPortalMagicLinkOutcome {
  redirectPath?: string;
  companyOptions?: ClientPortalCompanyOption[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  auth: { user: User; PermissionEnum: number[]; userRole: UserRole, employee?: Employee; clientId?: string; isClientUser?: boolean } = { user: null, PermissionEnum: [], userRole: null, isClientUser: false };
  hostname = window.location.hostname;
  projectId = this.hostname === 'localhost' ? environment.defaultProjectForLocalhost : this.getProjectIdFromHostname(this.hostname);

  urlAfterLogin: string = null;

  constructor(
    private api: ApiService,
    private angularFireAuth: AngularFireAuth,
    private spinnerService: SpinnerService,
    private toasterService: ToasterService,
    private navigationService: NavigationService,
    private db: DbService,
    private tokenService: TokenService,
    private translateService: TranslateService,
  ) { }

  getProjectIdFromHostname(hostname: string): string {
    // Firebase Hosting domains (e.g. pineapplecms.web.app) don't encode project slug.
    if (hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com')) {
      return environment.defaultProjectForLocalhost || 'pineapplelab';
    }

    const parts = hostname.split('.');

    // The dev admin domain is `app.dev.<tenant>`. `dev` identifies the
    // environment, so the tenant is the segment immediately after it.
    if (parts.length >= 3 && parts[0].toLowerCase() === 'app' && parts[1].toLowerCase() === 'dev') {
      return parts[2].toLowerCase();
    }

    // Handle country-code SLDs like .com.uy, .co.uk, .org.ar
    // e.g. paa.com.uy → ['paa','com','uy'] → effectiveParts=['paa'] → 'paa'
    // e.g. app.paa.com.uy → ['app','paa','com','uy'] → effectiveParts=['app','paa'] → 'paa'
    const ccSlds = ['com', 'org', 'net', 'edu', 'gov', 'co'];
    if (parts.length >= 3 && ccSlds.includes(parts[parts.length - 2])) {
      const effectiveParts = parts.slice(0, parts.length - 2);
      const domain = effectiveParts.length > 1 ? effectiveParts[1] : effectiveParts[0];
      return domain.toLowerCase();
    }
    const domain = parts.length > 2 ? parts[1] : parts[0];
    return domain.toLowerCase();
  }

  async initializeAuth() {
    if (isNullOrUndefined(this.auth.user) || this.auth.PermissionEnum.length === 0) {
      const firebaseUser = await this.angularFireAuth.user.pipe(take(1)).toPromise();
      if (isNullOrUndefined(firebaseUser?.uid)) {
        this.auth = { user: null, PermissionEnum: [], userRole: null, isClientUser: false };
        if (BrowserStorage.get('userToken')) {
          BrowserStorage.delete('userToken');
        }
        return;
      }
      try {
        const tokenProjectId = await this.getAuthPermissions(firebaseUser);
        if (tokenProjectId !== this.projectId) {
          await this.refreshSessionToken(this.projectId);
        }
        const user = await this.getSignedUserDoc(firebaseUser.uid);
        this.auth.user = user;
        // Re-mint the custom token if it is older than 24 hours so that any
        // role / permission changes made since the last login take effect.
        await this.maybeRefreshSessionToken(this.projectId);
      } catch {
        // A stale/invalid session (e.g. Firestore rules rejecting the read) must not
        // block app bootstrap via APP_INITIALIZER; fall back to a logged-out state.
        this.auth = { user: null, PermissionEnum: [], userRole: null, isClientUser: false };
        BrowserStorage.delete('userToken');
      }
    }
  }

  async login(
    user: { email?: string; username?: string; phone?: string; password: string },
    redirectAfterLogin?: string,
    overwriteUrlAfterLogin = false,
  ) {
    const payload = {
      email: user.email || null,
      username: user.username || null,
      phone: user.phone || null,
      password: user.password,
    };
    const isLocalHost = this.hostname === 'localhost' || this.hostname === '127.0.0.1';
    const requestOptions = isLocalHost && isNotEmptyString(this.projectId)
      ? { headers: { 'x-project-id': this.projectId } }
      : undefined;

    const result = await this.api.post(API_URLS.AUTH.LOGIN, payload, requestOptions).then(async (r) => {
      if (!isNotEmptyString(r?.accessToken) && (!isObject(r) || !isObject(r.data) || !isNotEmptyString(r.data.accessToken))) {
        return null;
      }
      const user = await this.signMeIn(r.accessToken || r.data.accessToken);
      await this.tokenService.getFirebaseToken();
      return user;
    });
    if (isNullOrUndefined(result)) {
      return result;
    }
    return result;
  }

  async signUp(user: SignUpDto) {
    return this.api.post(API_URLS.AUTH.SIGNUP, { ...user }).then((response) => {
      if (!response && !response.data) {
        return null;
      }
      return response;
    });
  }

  async signUpWithToken(signUp: SignUpTokenDto, token: string) {
    try {
      const response = await this.api.post(API_URLS.AUTH.SIGNUP_WITH_TOKEN(token), { ...signUp });
      if (!response && !response.data) {
        return null;
      }
      return response;
    } catch (error: any) {
      // Only a real expiry is reported as expired; the backend answers 409 for an existing account.
      const status = error?.status ?? error?.error?.statusCode;
      const message = error?.error?.message;
      const key = status === 409
        ? 'langs.invitation_account_exists'
        : message === 'The invitation has expired.'
          ? 'langs.expired_invitation_error'
          : 'langs.invitation_sign_up_error';
      this.toasterService.showError(this.translateService.instant(key));
    }
    return null;
  }

  async updatePassword(updatePasswordDto: UpdatePasswordDto) {
    return this.api.post(API_URLS.AUTH.UPDATE_PASSWORD, { ...updatePasswordDto });
  }
  async refreshPassword(updatePasswordDto: UpdatePasswordDto, loginDTO: LoginDTO) {
    return this.api.post(API_URLS.AUTH.REFRESH_PASSWORD, { ...updatePasswordDto, ...loginDTO });
  }

  async forgotPassword(forgotPassword: ForgotPasswordDTO) {
    return this.api.post(API_URLS.AUTH.FORGOT_PASSWORD, { ...forgotPassword }).then((response) => {
      if (!response && !response?.data) {
        return null;
      }
      return response;
    });
  }

  async saveExpiredToken(saveToken: LoginByAccessTokenIdDto) {
    return this.api.post(API_URLS.AUTH.SAVE_EXPIRED_TOKEN, { ...saveToken });
  }

  async getToken(accessToken: string, projectId: string) {
    return this.api.get(`${API_URLS.AUTH.EXPIRED_TOKEN}/${accessToken}/${projectId}`, null).then((response) => {
      if (!response && !response.data) {
        return null;
      }
      return response || response.data;
    });
  }

  async isSigned() {
    if (!isValidString(BrowserStorage.get('userToken'))) {
      return false;
    }
    if (this.auth.user || this.auth.isClientUser === true) {
      return true;
    }
    const firebaseUser = await this.angularFireAuth.user.pipe(take(1)).toPromise();
    if (isNullOrUndefined(firebaseUser)) {
      return false;
    }
    const user = await this.getSignedUserDoc(firebaseUser.uid);
    await this.getAuthPermissions(firebaseUser);
    // Client-portal users' `User` Firestore doc lives under THEIR project, which `this.projectId`
    // (hostname-derived) can't resolve on the shared pineapplecms.web.app domain — a valid
    // Firebase session + the isClientUser claim is enough; per-tenant data is fetched via REST
    // (server resolves the real project from the JWT), never from this doc lookup.
    const isClientUser = Boolean(this.auth.isClientUser);
    if (isClientUser) {
      return true;
    }
    return !isNullOrUndefined(user);
  }

  async signMeIn(token: string) {
    const response = await this.angularFireAuth.signInWithCustomToken(token);
    // Record when this custom token was minted so the 24-hour re-mint check
    // knows when permissions need to be refreshed.
    BrowserStorage.set('tokenMintedAt', String(Date.now()));
    BrowserStorage.set('userToken', token);
    // The backend signs the tenant into the Firebase token. Use that value for
    // the user lookup instead of deriving it a second time from the hostname.
    // This is important for hosts such as app.dev.<tenant>, where `dev` is an
    // environment segment and not the tenant id.
    const tokenProjectId = await this.getAuthPermissions(response.user);
    // Force-fetch the ID token so Firebase dispatches onIdTokenChanged and the
    // Firestore SDK reauths its WebChannel before any onSnapshot listener opens.
    // Without this, Firestore listeners opened in the very next ngOnInit can fire
    // before the SDK has refreshed its credentials → Missing or insufficient permissions.
    await response.user.getIdToken(/* forceRefresh */ true);
    return this.getSignedUserDoc(response.user.uid, tokenProjectId || this.projectId);
  }

  /**
   * If the current custom token is older than 24 hours, silently re-mints it
   * so any role / permission changes made by an admin take effect.
   */
  async maybeRefreshSessionToken(projectId: string = this.projectId): Promise<void> {
    const mintedAt = Number(BrowserStorage.get('tokenMintedAt') ?? '0');
    const twentyFourHours = 24 * 60 * 60 * 1000;
    if (mintedAt > 0 && Date.now() - mintedAt >= twentyFourHours) {
      await this.refreshSessionToken(projectId);
    }
  }

  async refreshSessionToken(projectId: string = this.projectId): Promise<void> {
    try {
      const r = await this.api.post(API_URLS.AUTH.REFRESH_SESSION_TOKEN, { projectId });
      const newToken: string = r?.accessToken || r?.data?.accessToken;
      if (isNotEmptyString(newToken)) {
        await this.signMeIn(newToken);
        await this.tokenService.getFirebaseToken();
      }
    } catch {
      // Non-critical: if the re-mint fails the existing session stays valid.
    }
  }

  /**
   * Redeems a one-time client-portal magic login link token. On success, signs the client-portal
   * user in via the existing custom-token flow (same as login()/signMeIn()) and returns the
   * server-chosen (allow-listed) redirect path. Throws on failure — callers should show a
   * generic, safe error message and never assume why redemption failed.
   *
   * `explicitProjectId`, when provided (parsed from the link's own `:projectId/:token` route
   * segment), always wins over `this.projectId` — the latter is only a best-effort hostname
   * guess and is wrong for links opened on the shared pineapplecms.web.app domain.
   */
  async redeemClientPortalMagicLink(token: string, explicitProjectId?: string): Promise<ClientPortalMagicLinkOutcome> {
    const projectId = isNotEmptyString(explicitProjectId) ? explicitProjectId : this.projectId;
    const r = await this.api.post(
      API_URLS.AUTH.CLIENT_PORTAL_MAGIC_LINK_REDEEM,
      { token, projectId },
      this.clientPortalLinkRequestOptions(projectId),
    );
    const options: ClientPortalCompanyOption[] = r?.companySelection?.options ?? r?.data?.companySelection?.options;
    if (Array.isArray(options)) {
      return { companyOptions: options };
    }
    return this.completeClientPortalSignIn(r);
  }

  /** Second step of a company-selection link (one phone, several companies). Signs in as the chosen company. */
  async selectClientPortalCompany(
    token: string,
    explicitProjectId: string | undefined,
    clientId: string,
    contactId: string,
  ): Promise<{ redirectPath: string }> {
    const projectId = isNotEmptyString(explicitProjectId) ? explicitProjectId : this.projectId;
    const r = await this.api.post(
      API_URLS.AUTH.CLIENT_PORTAL_SELECT_COMPANY,
      { token, projectId, clientId, contactId },
      this.clientPortalLinkRequestOptions(projectId),
    );
    return this.completeClientPortalSignIn(r);
  }

  private clientPortalLinkRequestOptions(projectId: string) {
    const isLocalHost = this.hostname === 'localhost' || this.hostname === '127.0.0.1';
    return isLocalHost && isNotEmptyString(projectId) ? { headers: { 'x-project-id': projectId } } : undefined;
  }

  private async completeClientPortalSignIn(r: any): Promise<{ redirectPath: string }> {
    const accessToken: string = r?.accessToken || r?.data?.accessToken;
    const redirectPath: string = r?.redirectPath || r?.data?.redirectPath;

    await this.signMeIn(accessToken);
    await this.tokenService.getFirebaseToken();
    return { redirectPath: isNotEmptyString(redirectPath) ? redirectPath : '/client-portal/bills' };
  }

  /**
   * Looks up the given project's active custom app domain (e.g. `app.pfelite.com`), or `null` if
   * none is configured/active. Used by the magic-link landing page to redirect off the shared
   * pineapplecms.web.app domain onto the tenant's own branded domain before redeeming. Never
   * throws — any failure is treated the same as "no custom domain".
   */
  async getProjectAppDomain(projectId: string): Promise<string | null> {
    try {
      const r = await this.api.get(API_URLS.AUTH.PUBLIC_PROJECT_APP_DOMAIN(projectId), new HttpParams());
      const domain: string | null = r?.domain ?? r?.data?.domain ?? null;
      return isNotEmptyString(domain) ? domain : null;
    } catch {
      return null;
    }
  }

  /**
   * Requests a passwordless WhatsApp login link for a client-portal user by phone number only.
   * The backend ALWAYS returns the same generic response (this method never throws for a
   * "not found"/ambiguous/rate-limited outcome, only for a genuine network failure) — the caller
   * must show the exact same fixed confirmation message regardless, and must never infer
   * anything about whether the phone is registered.
   *
   * `destination` is an OPTIONAL allow-listed KEY (e.g. `'bills'`) identifying where the client
   * was originally trying to go — NEVER a raw path/URL. The backend independently validates it
   * against its own fixed allow-list (see CLIENT_PORTAL_PHONE_LOGIN_DESTINATIONS), so an
   * unrecognized value here is harmless, but callers should only ever pass a value obtained from
   * CLIENT_PORTAL_REDIRECT_DESTINATIONS below.
   */
  async requestClientPortalPhoneLogin(phone: string, destination?: string): Promise<void> {
    const isLocalHost = this.hostname === 'localhost' || this.hostname === '127.0.0.1';
    const requestOptions = isLocalHost && isNotEmptyString(this.projectId)
      ? { headers: { 'x-project-id': this.projectId } }
      : undefined;
    // projectId is intentionally NEVER sent in the body — the backend resolves tenant from the
    // request host/subdomain only (the x-project-id header above is a localhost-only dev fallback,
    // same convention as redeemClientPortalMagicLink()).
    const body = isNotEmptyString(destination) ? { phone, destination } : { phone };
    await this.api.post(API_URLS.AUTH.CLIENT_PORTAL_PHONE_LOGIN_REQUEST, body, requestOptions);
  }

  /**
   * Self-service password creation/change for the CURRENTLY authenticated client-portal session.
   * Never sends a userId/projectId in the body — the backend derives both exclusively from the
   * caller's own verified token, so this can never set another user's password.
   */
  async setClientPortalPassword(password: string): Promise<void> {
    await this.api.post(API_URLS.AUTH.CLIENT_PORTAL_SET_PASSWORD, { password });
  }

  private async getAuthPermissions(user: firebase.User): Promise<string> {
    const idToken = await user.getIdTokenResult();
    this.auth.PermissionEnum = isArray(idToken.claims.permissions) ? idToken.claims.permissions : [];
    this.auth.clientId = idToken.claims.clientId ?? null;
    this.auth.isClientUser = idToken.claims.isClientUser === true;
    const projectId = idToken.claims.projectId as string;
    this.auth.userRole = await this.loadUserRole(projectId, user.uid);
    return projectId;
  }

  private async loadUserRole(projectId: string, userId: string): Promise<UserRole> {
    try {
      const doc = await this.db.firestore
        .collection(Project.PATH)
        .doc(projectId)
        .collection(UserRole.PATH)
        .doc(userId)
        .get();
      return new UserRole(doc.exists ? doc.data() : undefined);
    } catch {
      return new UserRole();
    }
  }

  get isClient(): boolean {
    return this.auth.isClientUser === true;
  }

  /**
   * True only for actual superAdmins (SUPERADMIN_READ_PROJECTS in the JWT claims).
   * Do NOT use hasPermission() for this — it short-circuits to true for any
   * user holding IS_ADMIN, which regular (non-super) Admins also have.
   */
  get isSuperAdmin(): boolean {
    return this.auth.PermissionEnum?.includes(PermissionEnum.SUPERADMIN_READ_PROJECTS) ?? false;
  }

  hasPermission(permission: PermissionEnum | PermissionEnum[], inclusive = true) {
    let access = false;
    if (!isNoEmptyArray(this.auth.PermissionEnum)) {
      return false;
    }
    if (isNullOrUndefined(permission)) {
      return false;
    }
    if (this.auth.PermissionEnum.includes(PermissionEnum.IS_ADMIN)) {
      return true;
    }
    if (!Array.isArray(permission)) {
      access = this.auth.PermissionEnum.includes(permission);
      return access;
    }
    if (inclusive) {
      access = permission.every((val, i) => this.auth.PermissionEnum.includes(val));
    } else {
      access = permission.some((p) => this.auth.PermissionEnum.includes(p));
    }
    return access;
  }

  /**
   * Returns true when the current user should only see clients explicitly assigned to them.
   * A user is restricted when they lack both IS_ADMIN and VIEW_ALL_CLIENTS.
   * (IS_ADMIN short-circuits inside hasPermission, so checking VIEW_ALL_CLIENTS is sufficient.)
   */
  isRestrictedToAssignedClients(): boolean {
    return !this.hasPermission(PermissionEnum.VIEW_ALL_CLIENTS);
  }

  hasPermissionAndAlert(permission: PermissionEnum | PermissionEnum[], inclusive = true) {
    const hasPermission = this.hasPermission(permission, inclusive);
    if (!hasPermission) {
      this.toasterService.showError(`You don't have enough permission!`);
    }
    return hasPermission;
  }

  async getSignedUserDoc(userId: string, projectId: string = this.projectId) {
    const userDoc = await this.db.firestore.collection(Project.PATH).doc(projectId).collection(User.PATH).doc(userId).get();
    if (userDoc.exists) {
      this.auth.user = new User({ ...userDoc.data(), defaultProject: projectId });
      return this.auth.user;
    }
    return null;
  }

  logout(avoidRedirect = false) {
    this.auth.user = null;
    this.auth.PermissionEnum = [];
    this.auth.isClientUser = false;
    BrowserStorage.delete('userToken');
    return this.angularFireAuth.signOut().finally(async () => {
      this.toasterService.show('Logout Successfully!', 'Success');
      if (avoidRedirect === false) {
        // The session is already cleared: never leave the user on a page that can no longer work.
        const navigated = await this.navigationService.goLogin().catch((): boolean => false);
        if (!navigated && location.pathname !== '/auth/login') {
          location.assign('/auth/login');
        }
      }
    });
  }

  async externalLogin(provider: 'facebook' | 'google' = 'google') {
    this.spinnerService.show();
    const user = await this.externalLoginFlow(provider).catch((e: any): null => null);
    this.spinnerService.hide();
    if (isNullOrUndefined(user)) {
      this.toasterService.show('Something wrong happen, try again!', 'Ok');
      return;
    }
    this.navigationService.goHome(this.auth.isClientUser === true);
  }

  private externalLoginPopup(provider: 'google' | 'facebook') {
    let authProvider = null;
    switch (provider) {
      case 'facebook':
        authProvider = new firebase.auth.FacebookAuthProvider();
        break;
      default:
        authProvider = new firebase.auth.GoogleAuthProvider();
        break;
    }
    return this.angularFireAuth.signInWithPopup(authProvider);
  }

  private externalAuth(model: Auth) {
    return this.api.externalAuth(model).then((r) => {
      if (!isObject(r) || !isObject(r.data) || !isNotEmptyString(r.data.accessToken)) {
        return null;
      }
      return this.signMeIn(r.data.accessToken);
    });
  }

  private async externalLoginFlow(provider: 'facebook' | 'google' = 'google') {
    const response: any = await this.externalLoginPopup(provider);
    const model = new Auth(this.projectId);
    model.firstName = response.additionalUserInfo.profile[provider === 'facebook' ? 'first_name' : 'given_name'];
    model.lastName = response.additionalUserInfo.profile[provider === 'facebook' ? 'last_name' : 'family_name'];
    model.avatar = this.getPictureUrlFromExternalProfile(response.additionalUserInfo.profile.picture);
    model.email = response.additionalUserInfo.profile.email;
    model.tokenId = await response.user.getIdToken();
    const user = await this.externalAuth(model).catch((e): null => null);
    return user;
  }

  private getPictureUrlFromExternalProfile(picture: any) {
    if (isValidString(picture)) {
      return picture;
    }
    if (!isObject(picture)) {
      return null;
    }
    if (isNullOrUndefined(picture.data)) {
      return null;
    }
    if (!isValidString(picture.data.url)) {
      return null;
    }
    return picture.data.url;
  }
}
